'use client';

import React from 'react';
import Link from 'next/link';

interface LandlordConsultationCTAProps {
  /** 'inline' = horizontal card, 'banner' = full-width strip, 'sidebar' = compact card */
  variant?: 'inline' | 'banner' | 'sidebar';
  className?: string;
}

export default function LandlordConsultationCTA({
  variant = 'inline',
  className = '',
}: LandlordConsultationCTAProps) {
  if (variant === 'banner') {
    return (
      <div className={`bg-[#0F2137] border-y border-[#1B6B5A]/30 py-8 px-6 ${className}`}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#1B6B5A]/30 flex items-center justify-center text-[#4DB896]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div>
              <p className="text-white font-semibold text-base leading-tight mb-1">
                Free 20-minute landlord consultation
              </p>
              <p className="text-[#8A9BB0] text-sm leading-relaxed max-w-lg">
                Speak directly with our team — no sales pitch, just honest advice on your property, your options, and what PDA can do for you.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              href="/contact?type=consultation"
              className="inline-flex items-center justify-center gap-2 bg-[#1B6B5A] hover:bg-[#155a4a] text-white font-semibold text-sm py-3 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap"
            >
              Book Free Consultation
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
            <a
              href="tel:01244380380"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white/80 hover:text-white font-medium text-sm py-3 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              01244 380 380
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className={`rounded-2xl bg-gradient-to-br from-[#0F2137] to-[#163050] border border-[#1B6B5A]/30 p-6 relative overflow-hidden ${className}`}>
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#1B6B5A] via-[#4DB896] to-[#1B6B5A]" aria-hidden="true" />
        <div className="w-9 h-9 rounded-lg bg-[#1B6B5A]/30 flex items-center justify-center text-[#4DB896] mb-4">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <p className="text-[#D4AF78] text-xs font-bold tracking-widest uppercase mb-2">Free Consultation</p>
        <h3 className="font-display text-base text-white font-medium leading-snug mb-2">
          Book a free 20-minute landlord consultation
        </h3>
        <p className="text-[#8A9BB0] text-xs leading-relaxed mb-4">
          Honest advice on your property, your options, and what switching to PDA looks like — no obligation.
        </p>
        <Link
          href="/contact?type=consultation"
          className="block text-center bg-[#1B6B5A] hover:bg-[#155a4a] text-white font-semibold text-xs py-2.5 px-4 rounded-lg transition-colors duration-200"
        >
          Book Now — It&apos;s Free
        </Link>
        <p className="text-[#8A9BB0] text-[10px] text-center mt-2">
          Or call <a href="tel:01244380380" className="text-white hover:text-[#4DB896] transition-colors">01244 380 380</a>
        </p>
      </div>
    );
  }

  // Default: inline
  return (
    <div className={`rounded-2xl border border-[#1B6B5A]/25 bg-gradient-to-r from-[#F0FDF8] to-[#F7F6F3] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 ${className}`}>
      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#1B6B5A]/15 flex items-center justify-center text-[#1B6B5A]">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#0F2137] text-sm leading-tight mb-1">
          Book a free 20-minute landlord consultation
        </p>
        <p className="text-[#6B7280] text-xs leading-relaxed">
          Speak with our team about your property, your current agent, or your options — no obligation, no sales pitch.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
        <Link
          href="/contact?type=consultation"
          className="inline-flex items-center justify-center gap-1.5 bg-[#1B6B5A] hover:bg-[#155a4a] text-white font-semibold text-xs py-2.5 px-5 rounded-lg transition-colors duration-200 whitespace-nowrap"
        >
          Book Free Consultation
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
        </Link>
        <a
          href="tel:01244380380"
          className="inline-flex items-center justify-center gap-1.5 border border-[#1B6B5A]/30 text-[#1B6B5A] hover:bg-[#1B6B5A]/5 font-medium text-xs py-2.5 px-4 rounded-lg transition-colors duration-200 whitespace-nowrap"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          Call Us
        </a>
      </div>
    </div>
  );
}
